
import TimeUnit from "./TimeUnit.js";
import ZonedClock from "./ZonedClock.js";
import DateUtils from "@/utils/DateUtils.js";
import RcDay from "./RcDay.js";
import RcYear from "./RcYear.js";
import RcQuarterYear from "./RcQuarterYear.js";

/** A month, mirroring com.readychek.time.RcMonth. */
export default class RcMonth extends TimeUnit {

  constructor(date, timeZone) {
    super(date, timeZone);
  }

  /** The month, counted from zero, matching java's Calendar.MONTH. */
  value() {
    return this.monthValue();
  }

  name(abbreviated) {
    return DateUtils.monthName(this.value(), abbreviated);
  }

  year() {
    return new RcYear(this.internalDate(), this.timeZone());
  }

  day() {
    return new RcDay(this.internalDate(), this.timeZone());
  }

  quarterYear() {
    return new RcQuarterYear(this.internalDate(), this.timeZone());
  }

  /** The quarter of the year this month falls in, counted from zero. */
  quarterValue() {
    return Math.floor(this.value() / 3);
  }

  /** How many days this month has. */
  days() {
    return ZonedClock.daysInMonth(this.year().value(), this.value());
  }

  /** The first day of this month. */
  first() {
    return new RcDay(
      ZonedClock.withFields(this.internalDate(), this.timeZone(), { day: 1 }),
      this.timeZone(),
    );
  }

  /** The last day of this month. */
  last() {
    return new RcDay(
      ZonedClock.withFields(this.internalDate(), this.timeZone(), { day: this.days() }),
      this.timeZone(),
    );
  }

  firstDay() {
    return this.first();
  }

  lastDay() {
    return this.last();
  }

  previous() {
    return this.shift(-1);
  }

  next() {
    return this.shift(1);
  }

  plus(numberOfMonths) {
    let result = this;
    for (let i = 0; i < numberOfMonths; i++) {
      result = result.next();
    }
    return result;
  }

  minus(numberOfMonths) {
    let result = this;
    for (let i = 0; i < numberOfMonths; i++) {
      result = result.previous();
    }
    return result;
  }

  /**
   * Steps whole months, clamping the day so a month the target does not have cannot roll
   * the date into the following one - the 31st of March stepped back lands on 28 February,
   * not the 3rd of March.
   */
  shift(months) {
    const parts = this.parts();
    const total = parts.year * 12 + parts.month + months;
    const year = Math.floor(total / 12);
    const month = ((total % 12) + 12) % 12;
    const day = Math.min(parts.day, ZonedClock.daysInMonth(year, month));
    return new RcMonth(
      ZonedClock.withFields(this.internalDate(), this.timeZone(), { year, month, day }),
      this.timeZone(),
    );
  }
}
